<template>
  <div
    :class="{
      disabledComponent: isProjectCancelled,
    }"
  >
    <div class="mt-4">
      <div
        v-if="!deliverables?.length"
        class="d-flex flex-column justify-content-center align-items-center"
        style="min-height: 200px"
      >
        <h4>No deliverables yet</h4>
        <p>All deliverables added in each timeline will be listed here</p>
      </div>
      <ProjectDeliverable
        v-for="(deliverable, index) in deliverables"
        :ref="`project-deliverable-${index}`"
        :key="index"
        :index="index"
        :deliverable="deliverable"
        :project="project"
        :is-dirty="isDirty(index)"
        @preview="(emailTemplate) => preview(deliverable, index, emailTemplate)"
        @send="
          (emailTemplate) => sendDeliverable(deliverable, index, emailTemplate)
        "
        :readonly="readonly"
        :is-brief="isBrief"
        :project-workflows="workflows"
        :disableButton="disableButton"
      />
    </div>
    <div class="mt-3" v-if="!readonly && $auth.user.isAgent">
      <button
        @click="eventBus.$emit(channels.addDeliverable)"
        type="button"
        class="btn btn-primary float-right"
        title="Add Deliverable"
        :disabled="isSubscriptionReadOnly() || isArchived || disableButton"
      >
        <i class="fa fa-plus"></i><span>Add Deliverable</span>
      </button>
    </div>
  </div>
</template>

<script>
import timeMixin from "../../mixins/time";
import ProjectDeliverablePreviewModal from "./ProjectDeliverablePreviewModal.vue";
import ProjectDeliverable from "@/components/projects/ProjectDeliverable.vue";
import eventBus, { channels } from "@/eventBus";
import { getProjectById } from "@/apis/projects";

export default {
  name: "ProjectDeliverables",
  components: { ProjectDeliverable },
  mixins: [timeMixin],
  props: {
    deliverables: Array,
    originalDeliverablesString: String,
    project: [Object, Array], // Needed for some details in the Deliverables preview modal
    readonly: Boolean,
    isBrief: Boolean,
  },
  data() {
    return {
      workflows: [],
      disableButton: false,
    };
  },
  computed: {
    isProjectCancelled() {
      return this.project.cancelled;
    },
    isArchived() {
      return Boolean(this.project.archived);
    },
    channels() {
      return channels;
    },
    eventBus() {
      return eventBus;
    },
  },
  methods: {
    isDirty(i) {
      if (this.originalDeliverablesString == null) return false;
      const arr = JSON.parse(this.originalDeliverablesString);
      return JSON.stringify(arr[i]) !== JSON.stringify(this.deliverables[i]);
    },
    sendDeliverable(deliverable, index, emailTemplate) {
      this.disableButton = true;
      const d = {
        ...deliverable,
        workflow_id: +(
          this.$route.params.workflowId ?? this.project.workflow_id
        ),
      };
      delete d.files;
      delete d.type;

      this.$refs[`project-deliverable-${index}`]?.[0]?.setMode("preview");

      eventBus.$emit(channels.sendDeliverable, {
        deliverable: d,
        templateId: emailTemplate.id,
        isDirty: this.isDirty(index),
      });
      this.disableButton = false;
    },
    /**
     * Public method used from parent page to open a particular row for editing (used on creating a new valuation)
     */
    preview(deliverable, index, emailTemplate) {
      const modalOptions = {
        name: "project-deliverables-preview-modal",
        height: "auto",
      };
      this.$modal.show(
        ProjectDeliverablePreviewModal,
        {
          deliverable,
          template: emailTemplate.body,
          language: emailTemplate.language,
          project: this.project,
          disableButton: this.disableButton,
        },
        modalOptions,
        {
          "before-close": async (e) => {
            const newValue = e.params?.value;
            if (!newValue) return;

            if (deliverable.type === "link" && !deliverable.link) {
              this.notifyError("Deliverable link is required");
              return;
            }
            this.sendDeliverable(deliverable, index, emailTemplate);
            eventBus.$emit(channels.saveDeliverable, index);
          },
        }
      );
    },
  },
  async mounted() {
    if (this.isBrief) {
      const project = await getProjectById(this.project.id);

      this.workflows = project.projectWorkflows?.map((pw) => ({
        id: pw.workflow_id,
        title: pw.workflow_title,
      }));
    }
  },
};
</script>

<style scoped>
h3 {
  color: rgb(74, 76, 78);
  font-weight: normal;
}
</style>
